import React from "react";
import { useTranslation } from "react-i18next";
import {
  AcademicCapIcon,
  PencilSquareIcon,
  UserGroupIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { intlLocaleFor } from "../../../../i18n/languages";

/**
 * Shared building blocks for HMS document compliance (PROMANG_COMPONENTS.md §8 «Etterlevelse»):
 * where access comes from, read status, deadline, postponements, KPI tiles and progress.
 */

const SOURCE_META = {
  manual: { icon: UserIcon, label: "hseCompliance.source.manual" },
  audience: { icon: UserGroupIcon, label: "hseCompliance.source.audience" },
  training: { icon: AcademicCapIcon, label: "hseCompliance.source.training" },
  creator: { icon: PencilSquareIcon, label: "hseCompliance.source.creator" },
};

/** Chips for access sources. Training chips name the guide(s) when known. */
export const ComplianceSourceChips = ({ sources = [], trainingGuides = [], emptyLabel }) => {
  const { t } = useTranslation();
  const list = sources.filter((source) => SOURCE_META[source]);
  if (!list.length) {
    return emptyLabel ? <span className="text-[12.5px] text-[color:var(--pls-faint)]">{emptyLabel}</span> : null;
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {list.map((source) => {
        const meta = SOURCE_META[source];
        const Icon = meta.icon;
        if (source === "training" && trainingGuides.length) {
          return trainingGuides.map((guide) => (
            <span key={`training-${guide.id}`} className="pm-source-chip pm-source-chip--training" title={guide.title}>
              <Icon aria-hidden="true" />
              {t("hseCompliance.source.training_named", { title: guide.title })}
            </span>
          ));
        }
        return (
          <span key={source} className={`pm-source-chip pm-source-chip--${source}`}>
            <Icon aria-hidden="true" />
            {t(meta.label)}
          </span>
        );
      })}
    </span>
  );
};

const STATUS_META = {
  acknowledged: { badge: "pm-badge--ok", label: "hseCompliance.status.acknowledged" },
  pending: { badge: "pm-badge--warn", label: "hseCompliance.status.pending" },
  overdue: { badge: "pm-badge--bad", label: "hseCompliance.status.overdue" },
  optional: { badge: "", label: "hseCompliance.status.optional" },
  no_access: { badge: "", label: "hseCompliance.status.no_access" },
  exempt: { badge: "pm-badge--accent", label: "hseCompliance.status.exempt" },
};

export const ComplianceStatusBadge = ({ status, via }) => {
  const { t } = useTranslation();
  const meta = STATUS_META[status] || STATUS_META.optional;
  const viaLabel = status === "acknowledged" && via && via !== "direct" ? t(`hseCompliance.via.${via}`) : null;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className={`pm-badge ${meta.badge}`.trim()}>{t(meta.label)}</span>
      {viaLabel ? <span className="text-[11.5px] text-[color:var(--pls-muted)]">{viaLabel}</span> : null}
    </span>
  );
};

const SOON_MS = 3 * 24 * 60 * 60 * 1000;

export const formatComplianceDate = (value, locale) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  // App codes are not always BCP 47 (`se` is Swedish here, Northern Sami in Intl).
  return date.toLocaleDateString(locale ? intlLocaleFor(locale) : undefined, { day: "numeric", month: "short", year: "numeric" });
};

/** Deadline cell: date + FORFALT / SNART flag. */
export const ComplianceDueDate = ({ dueAt, overdue, now = Date.now() }) => {
  const { t, i18n } = useTranslation();
  if (!dueAt) return <span className="text-[color:var(--pls-faint)]">—</span>;
  const time = new Date(dueAt).getTime();
  const isOverdue = overdue ?? time < now;
  const soon = !isOverdue && time - now <= SOON_MS;
  const tone = isOverdue ? "pm-due--overdue" : soon ? "pm-due--soon" : "";
  return (
    <span className={`pm-due ${tone}`.trim()}>
      <span>{formatComplianceDate(dueAt, i18n.language)}</span>
      {isOverdue ? <span className="pm-due__flag">{t("hseCompliance.due.overdue_flag")}</span> : null}
      {soon ? <span className="pm-due__flag">{t("hseCompliance.due.soon_flag")}</span> : null}
    </span>
  );
};

/** ●●○ — used postponements filled. */
export const PostponementDots = ({ used = 0, max = 0 }) => {
  const { t } = useTranslation();
  if (!max) return null;
  return (
    <span
      className="pm-postpone-dots"
      role="img"
      aria-label={t("hseCompliance.postpone.dots_label", { used, max })}
      title={t("hseCompliance.postpone.dots_label", { used, max })}
    >
      {Array.from({ length: max }, (_, index) => (
        <span key={index} className={`pm-postpone-dots__dot${index < used ? " is-used" : ""}`} />
      ))}
    </span>
  );
};

/** KPI tile; pass onClick + pressed to use it as a filter. */
export const ComplianceKpi = ({ label, value, hint, tone, onClick, pressed }) => {
  const className = `pm-kpi${tone ? ` pm-kpi--${tone}` : ""}`;
  const body = (
    <>
      <div className="pm-kpi__label">{label}</div>
      <div className="pm-kpi__value">{value}</div>
      {hint ? <div className="pm-kpi__hint">{hint}</div> : null}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={className} aria-pressed={Boolean(pressed)} onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
};

export const ComplianceProgress = ({ value = 0, total = 0, label }) => {
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <span className={`pm-progress${total > 0 && value >= total ? " pm-progress--ok" : ""}`} title={label}>
      <span
        className="pm-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={label}
      >
        <span className="pm-progress__fill" style={{ width: `${percent}%` }} />
      </span>
      <span className="pm-progress__value">{total > 0 ? `${percent} %` : "—"}</span>
    </span>
  );
};
